/**
 * PatientChartPage — the chart as ONE page with horizontal TABS (the prototype's patient chart, 19 Oct; it replaced the
 * one-page chart with the still index, 15 Oct). A one-line summary of the record sits at the top, the tabs under it
 * (Overview · Documents · Medications · Problems & allergies · Labs…), each with the count of rows it holds, and the tab in
 * view shows its sections in full (no 5-row cap; each section keeps its own paging). Same tab bodies, same hooks, same data:
 * only the arrangement changes.
 *
 * Only the tab in view mounts its body, so opening the chart loads one tab's data at a time (as main's chart did, one
 * section at a time) instead of firing every section's queries at once. A tab once opened stays mounted (hidden) after a
 * switch, so a half-typed form inside it survives and its count stays beside its name; a tab not yet opened shows no
 * count. The tabs are the app's shared underline tabs
 * (`HeaderTabs`: the ones that do not fit go into "More", the active tab always stays).
 *
 * Three layouts. `stack` (the default; no caller since 23 Oct): the tab in view under the tabs, the page scrolled by its
 * host. `track` (no caller since 23 Oct; was the patient record's Chart tab, Antonin's export 21 Oct): the
 * summary line and the tabs stay fixed at the top, and under them a horizontal snapping track holds one column per tab
 * in view: the lead tab (the Overview) first, then the tab in view; each column scrolls itself. The tabs jump along the
 * track. Visited tabs stay mounted in both layouts (hidden columns in the track), so counts and half-typed forms survive.
 *
 * `index` (the patient record's and the encounter's Chart tabs, Antonin's export 23 Oct, "Patient · Chart": `PatientWorkspaceScreen` in the
 * export's kit): a sticky index of every tab on the left (name + count, the one in view marked), and every tab's sections on
 * ONE scrolling page beside it, each tab a block under its own title with a hairline between blocks. Every block mounts at
 * once (the page is one scroll), so every section's queries run on open. A click on the index glides to the block; the
 * index follows the scroll. The Overview stays the first block (Ahmed, 23 Oct; the export's index has no Overview).
 *
 * Layout and look live in the bridge stylesheet under the "patient chart" heading (stack) and in `showcase-chart.css`
 * (track, index), keyed on `data-slot`.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { HeaderTabs, type HeaderTab } from '@/shared/ui/components/header-tabs';
import { ChartCountContext, ChartPageContext, sectionDomId } from './chart-page-context';
import { ChartUncappedContext } from './use-row-cap';

/** One section of a tab: the page draws its heading and the section's own actions on the same row. */
export interface ChartPagePart {
  key: string;
  title: string;
}

export interface ChartPageTab {
  key: string;
  /** The tab's name (the count follows it). */
  label: string;
  /** The sections the tab holds, in order. A tab with one section hides that heading (the tab names it). */
  parts?: ChartPagePart[];
  /** The sections whose rows the tab's count adds up (default: all of them). Medications counts the medications, not the
   *  drug interactions shown under them. */
  countParts?: string[];
  /** A body of its own (the Overview) instead of sections. Such a tab shows no count. */
  render?: () => ReactNode;
  /** Track layout only: the tab is also a column before the tab in view (the Overview), so it is always mounted. */
  lead?: boolean;
}

export type ChartPageLayout = 'stack' | 'track' | 'index';

export interface PatientChartPageProps {
  tabs: ChartPageTab[];
  /** The key of the tab in view. */
  active: string;
  onActiveChange: (key: string) => void;
  /** Draws one section's body (the tab body of the section's key). */
  renderPart: (key: string) => ReactNode;
  /** The line over the tabs (true facts only; `ChartMeta` draws its own paragraph, or nothing). */
  meta?: ReactNode;
  /** `stack` (default): the tab in view under the tabs. `track`: fixed head, one snapping column per tab in view.
   *  `index`: a sticky index on the left, every tab on one scrolling page. */
  layout?: ChartPageLayout;
}

const titleDomId = (key: string) => `chart-section-title-${key}`;

/** The element that scrolls the page: the nearest ancestor that scrolls vertically (the page body), else none. */
function scrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let p = el?.parentElement ?? null; p; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if (o === 'auto' || o === 'scroll') return p;
  }
  return null;
}

/** Track layout: scrolls the track so the tab's column is at its left, and that column to its top (a short tab never
 *  lands under the scroll it was left at). A column not on the track (hidden) is left alone. */
function scrollToColumn(track: HTMLElement | null, key: string, smooth: boolean, toTop: boolean): boolean {
  const col = track?.querySelector<HTMLElement>(`[data-col="${key}"]`);
  if (!track || !col || col.hidden) return false;
  track.scrollTo?.({ left: col.offsetLeft - track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
  if (toTop) col.scrollTo?.({ top: 0 });
  return true;
}

/** Index layout: how far under the page's top edge a block counts as the one in view, and the gap a jump leaves above it. */
const INDEX_SPY_OFFSET = 24;
const INDEX_JUMP_GAP = 16;

/** Index layout: scrolls the page so the tab's block starts just under its top edge (or as far as the page goes). Returns
 *  the `scrollTop` the page is heading for, or null when it is already there (nothing will scroll). */
function scrollToBlock(page: HTMLElement | null, key: string, smooth: boolean): number | null {
  const block = page?.querySelector<HTMLElement>(`[data-chart-block="${key}"]`);
  if (!page || !block) return null;
  const top = block.getBoundingClientRect().top - page.getBoundingClientRect().top + page.scrollTop - INDEX_JUMP_GAP;
  const target = Math.max(0, Math.min(top, page.scrollHeight - page.clientHeight));
  page.scrollTo?.({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
  return Math.abs(target - page.scrollTop) >= 1 ? target : null;
}

/** Index layout: the page is scrolled to its end (a short last block can never reach the top edge). */
const atEnd = (page: HTMLElement) => page.scrollHeight > page.clientHeight && page.scrollTop + page.clientHeight >= page.scrollHeight - 1;

export function PatientChartPage({ tabs, active, onActiveChange, renderPart, meta, layout = 'stack' }: PatientChartPageProps) {
  const track = layout === 'track';
  /** Rows each section holds, told by the sections (the count beside the tab's name). */
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const reportCount = useCallback(
    (key: string, count: number | null) => setCounts((prev) => (prev[key] === count ? prev : { ...prev, [key]: count })),
    [],
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  /** The tab whose column `select` already started gliding to: the effect leaves the track alone for it (a jump would cut the glide). */
  const glidingTo = useRef<string | null>(null);
  /** The tabs the track always shows: the lead columns (the Overview), first. None in the stack layout. */
  const leadKeys = track ? tabs.filter((t) => t.lead).map((t) => t.key) : [];
  /** The tabs opened so far in this visit: their bodies stay mounted (hidden) once opened. Grown during render, the
   *  React pattern for state derived from a prop change, so the new tab mounts in the same pass. */
  const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set([active, ...leadKeys]));
  const unseen = [active, ...leadKeys].filter((k) => !visited.has(k));
  if (unseen.length > 0) setVisited(new Set([...visited, ...unseen]));

  // Track layout: the tab in view is the column at the track's left (instantly on open and on every switch; a tab click
  // has already started the smooth scroll where its column was on the track). Syncs the DOM scroll, not React state.
  useEffect(() => {
    if (!track) return;
    if (glidingTo.current === active) {
      // `select` already started the smooth scroll to this column: only the column's own scroll resets.
      glidingTo.current = null;
      trackRef.current?.querySelector<HTMLElement>(`[data-col="${active}"]`)?.scrollTo?.({ top: 0 });
      return;
    }
    scrollToColumn(trackRef.current, active, false, true);
  }, [track, active]);

  const index = layout === 'index';
  /** Index layout: the block the index marks. Follows the scroll; a new `active` (a click, a link, the URL) resets it,
   *  during render, so the mark moves in the same pass. */
  const [current, setCurrent] = useState(active);
  const [shownActive, setShownActive] = useState(active);
  if (shownActive !== active) {
    setShownActive(active);
    setCurrent(active);
  }
  // Index layout: the page shows the tab in view's block (on open and when the tab changes from outside, instantly; a
  // click on the index has already started the glide). Syncs the DOM scroll, not React state.
  useEffect(() => {
    if (!index) return;
    if (glidingTo.current === active) {
      glidingTo.current = null;
      return;
    }
    scrollToBlock(rootRef.current, active, false);
  }, [index, active]);

  /** Index layout: the `scrollTop` a click is gliding to. While set, the scroll does not move the mark (the glide passes
   *  every block between, up or down); it clears when the page arrives (within 1px), on `scrollend`, or when the reader
   *  takes over (wheel, touch, a key: Safari before 26 fires no `scrollend`). */
  const jumpingTo = useRef<number | null>(null);
  const releaseJump = () => {
    jumpingTo.current = null;
  };

  /** Index layout: the block in view is the last one whose top has reached the page's top edge; at the page's end, the
   *  last block (a short one never reaches the top). */
  const followScroll = () => {
    const page = rootRef.current;
    if (!page) return;
    const blocks = Array.from(page.querySelectorAll<HTMLElement>('[data-chart-block]'));
    const pageTop = page.getBoundingClientRect().top;
    const jumping = jumpingTo.current;
    if (jumping != null) {
      if (Math.abs(page.scrollTop - jumping) <= 1) releaseJump();
      return;
    }
    let inView: string | undefined = tabs[0]?.key;
    if (atEnd(page)) inView = blocks.at(-1)?.dataset.chartBlock ?? inView;
    else {
      for (const block of blocks) {
        if (block.getBoundingClientRect().top <= pageTop + INDEX_SPY_OFFSET) inView = block.dataset.chartBlock;
      }
    }
    if (inView && inView !== current) setCurrent(inView);
  };

  const jump = (key: string) => {
    setCurrent(key);
    jumpingTo.current = scrollToBlock(rootRef.current, key, true);
    if (key === active) return;
    glidingTo.current = key;
    onActiveChange(key);
  };

  /** What a tab holds: the sum of its counted sections' rows, nothing while one still loads (a half count would mislead). */
  const countOf = (tab: ChartPageTab): number | null => {
    if (!tab.parts || tab.parts.length === 0) return null;
    let sum = 0;
    for (const p of tab.parts) {
      if (tab.countParts && !tab.countParts.includes(p.key)) continue;
      const c = counts[p.key];
      if (c == null) return null;
      sum += c;
    }
    return sum;
  };

  const headerTabs: HeaderTab<string>[] = tabs.map((t) => {
    const count = countOf(t);
    return {
      id: t.key,
      label:
        count == null ? (
          t.label
        ) : (
          <>
            {t.label} <span className="header-tab-count">{count}</span>
          </>
        ),
    };
  });

  const select = (key: string) => {
    if (track && key === active) {
      // The tab already in view, but the track may have been scrolled back to the Overview: glide to its column again
      // (no host call: the tab did not change).
      scrollToColumn(trackRef.current, key, true, false);
      return;
    }
    if (key === active) return;
    if (track) {
      // Each column scrolls itself, so the new tab opens at its own top (the effect above); here the track glides to the
      // column when it is already on the track (the Overview).
      glidingTo.current = scrollToColumn(trackRef.current, key, true, false) ? key : null;
      onActiveChange(key);
      return;
    }
    onActiveChange(key);
    // The new tab opens at its top: a short tab must not land under the scroll the last one left.
    scrollParent(rootRef.current)?.scrollTo?.({ top: 0 });
  };

  /** A tab's body: its own (the Overview), or its sections, each under the heading the page draws. */
  const tabBody = (tab: ChartPageTab): ReactNode =>
    tab.render
      ? tab.render()
      : (tab.parts ?? []).map((part) => (
          <section
            key={part.key}
            id={sectionDomId(part.key)}
            data-slot="patient-chart-section"
            data-section-key={part.key}
            data-section-label={part.title}
            data-solo={(tab.parts?.length ?? 0) === 1 ? 'true' : undefined}
            aria-labelledby={titleDomId(part.key)}
          >
            <h2 id={titleDomId(part.key)} data-slot="patient-chart-section-title">
              {part.title}
            </h2>
            <ChartPageContext.Provider value={part.key}>{renderPart(part.key)}</ChartPageContext.Provider>
          </section>
        ));

  /** Index layout: a tab's block. Its first section carries the tab's name as the block's title (its actions on that
   *  row); a further section (Allergies under Problems) keeps its own name as a sub-title. A tab with its own body (the
   *  Overview) gets the title over it. */
  const indexBlock = (tab: ChartPageTab): ReactNode => (
    <div
      key={tab.key}
      role="region"
      aria-labelledby={titleDomId(tab.render ? tab.key : (tab.parts?.[0]?.key ?? tab.key))}
      data-slot="chart-index-block"
      data-chart-block={tab.key}
    >
      {tab.render ? (
        <>
          <h2 id={titleDomId(tab.key)} data-slot="patient-chart-section-title">
            {tab.label}
          </h2>
          {tab.render()}
        </>
      ) : (
        (tab.parts ?? []).map((part, i) => (
          <section
            key={part.key}
            id={sectionDomId(part.key)}
            data-slot="patient-chart-section"
            data-section-key={part.key}
            data-section-label={part.title}
            data-sub={i > 0 ? 'true' : undefined}
            aria-labelledby={titleDomId(part.key)}
          >
            {i === 0 ? (
              <h2 id={titleDomId(part.key)} data-slot="patient-chart-section-title">
                {tab.label}
              </h2>
            ) : (
              <h3 id={titleDomId(part.key)} data-slot="patient-chart-section-title">
                {part.title}
              </h3>
            )}
            <ChartPageContext.Provider value={part.key}>{renderPart(part.key)}</ChartPageContext.Provider>
          </section>
        ))
      )}
    </div>
  );

  if (index) {
    return (
      <div
        ref={rootRef}
        data-slot="patient-chart-page"
        data-layout="index"
        onScroll={followScroll}
        onScrollEnd={releaseJump}
        onWheel={releaseJump}
        onTouchStart={releaseJump}
        onKeyDown={releaseJump}
      >
        <nav data-slot="chart-index" aria-label="Chart sections">
          {tabs.map((tab) => {
            const count = countOf(tab);
            return (
              <button
                key={tab.key}
                type="button"
                data-slot="chart-index-item"
                aria-current={tab.key === current ? 'true' : undefined}
                onClick={() => jump(tab.key)}
              >
                <span>{tab.label}</span>
                {count != null ? <span data-slot="chart-index-count">{count}</span> : null}
              </button>
            );
          })}
        </nav>
        <div data-slot="chart-index-sections">
          {meta}
          <ChartUncappedContext.Provider value>
            <ChartCountContext.Provider value={reportCount}>{tabs.map(indexBlock)}</ChartCountContext.Provider>
          </ChartUncappedContext.Provider>
        </div>
      </div>
    );
  }

  const tabsRow = (
    <div data-slot="chart-tabs">
      <HeaderTabs tabs={headerTabs} value={active} onChange={select} aria-label="Chart" />
    </div>
  );

  if (track) {
    // Lead columns first (the Overview), then the others in tab order; only the lead ones and the tab in view are on the
    // track, the other visited ones stay mounted out of it.
    const ordered = [...tabs.filter((t) => t.lead), ...tabs.filter((t) => !t.lead)];
    return (
      <div ref={rootRef} data-slot="patient-chart-page" data-layout="track">
        <div data-slot="patient-chart-head">
          {meta}
          {tabsRow}
        </div>
        <ChartUncappedContext.Provider value>
          <ChartCountContext.Provider value={reportCount}>
            <div ref={trackRef} data-slot="chart-columns">
              {ordered.map((tab) => (
                <div
                  key={tab.key}
                  role="tabpanel"
                  aria-label={tab.label}
                  data-slot="patient-chart-panel"
                  data-tab-key={tab.key}
                  data-col={tab.key}
                  hidden={tab.key !== active && !tab.lead}
                >
                  {visited.has(tab.key) ? tabBody(tab) : null}
                </div>
              ))}
            </div>
          </ChartCountContext.Provider>
        </ChartUncappedContext.Provider>
      </div>
    );
  }

  return (
    <div ref={rootRef} data-slot="patient-chart-page">
      {meta}
      {tabsRow}
      <ChartUncappedContext.Provider value>
        <ChartCountContext.Provider value={reportCount}>
          {tabs.map((tab) => (
            <div
              key={tab.key}
              role="tabpanel"
              aria-label={tab.label}
              data-slot="patient-chart-panel"
              data-tab-key={tab.key}
              hidden={tab.key !== active}
            >
              {visited.has(tab.key) ? tabBody(tab) : null}
            </div>
          ))}
        </ChartCountContext.Provider>
      </ChartUncappedContext.Provider>
    </div>
  );
}
